/**
 * Language Switcher Component
 *
 * Toggle button for switching between Russian and English.
 * Designed for compact placement in version-info or header.
 *
 * @example
 * <LanguageSwitcher />
 * <LanguageSwitcher variant="dropdown" />
 */

import React from 'react';
import { useTranslation } from '../i18n';
import './LanguageSwitcher.css';

/**
 * @typedef {'toggle' | 'dropdown'} SwitcherVariant
 */

/**
 * Language Switcher Component
 *
 * @param {Object} props
 * @param {SwitcherVariant} [props.variant='toggle'] - UI variant
 * @param {string} [props.className] - Additional CSS class
 */
function LanguageSwitcher({ variant = 'toggle', className = '' }) {
  const { language, setLanguage } = useTranslation();

  const toggleLanguage = () => {
    setLanguage(language === 'ru' ? 'en' : 'ru');

    // Haptic feedback if available
    try {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    } catch (e) {
      // Ignore
    }
  };

  if (variant === 'dropdown') {
    return (
      <select
        className={`language-switcher language-switcher--dropdown ${className}`}
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
      >
        <option value="ru">🇷🇺 Русский</option>
        <option value="en">🇬🇧 English</option>
      </select>
    );
  }

  // Default: toggle variant
  return (
    <button
      className={`language-switcher language-switcher--toggle ${className}`}
      onClick={toggleLanguage}
      title={language === 'ru' ? 'Switch to English' : 'Переключить на русский'}
    >
      <span className={`lang-option ${language === 'ru' ? 'active' : ''}`}>RU</span>
      <span className="lang-separator">/</span>
      <span className={`lang-option ${language === 'en' ? 'active' : ''}`}>EN</span>
    </button>
  );
}

export default LanguageSwitcher;
